/**
 * Mobile-web → web app routing, shared by the middleware (homepage redirect) and
 * the client-side store-link interceptor.
 *
 * Mobile browsers are sent to the Flutter web app instead of the marketing site
 * or the Play Store / App Store. The target is picked from the request host at
 * runtime rather than from an env var, because NEXT_PUBLIC_* values are baked in
 * at build time and the stage build is not guaranteed to carry its own copy.
 */

const PROD_WEB_APP_URL = 'https://app.dharmayana.in/';
const STAGE_WEB_APP_URL = 'https://app.stage.dharmayana.in/';

const PROD_HOSTS = ['dharmayana.in', 'www.dharmayana.in'];

/** Web app URL for the environment that served `host` (stage for anything non-prod). */
export const getWebAppUrl = (host: string | null | undefined): string => {
  const hostname = (host || '').split(':')[0].toLowerCase();
  return PROD_HOSTS.includes(hostname) ? PROD_WEB_APP_URL : STAGE_WEB_APP_URL;
};

/** Same device test as AppContext's isMobileDevice. */
export const isMobileUserAgent = (ua: string) => /Android|iPhone|iPad|iPod/i.test(ua);

/** The native app's own WebView — it must keep rendering the site, never redirect. */
export const isAppWebViewUserAgent = (ua: string) => /AppWebView/i.test(ua);

// Crawlers and link-preview fetchers (Googlebot smartphone, Lighthouse, WhatsApp
// unfurls) send mobile UAs; redirecting them would de-index or mis-preview the site.
const BOT_UA = /bot|crawl|spider|slurp|lighthouse|facebookexternalhit|whatsapp|telegram|preview/i;

/** A real person on a mobile browser — the audience that should land on the web app. */
export const shouldUseWebApp = (ua: string | null | undefined): boolean => {
  const userAgent = ua || '';
  return isMobileUserAgent(userAgent) && !isAppWebViewUserAgent(userAgent) && !BOT_UA.test(userAgent);
};

// smart.link is the Kochava attribution link that fronts both stores.
const STORE_HOSTS = ['play.google.com', 'apps.apple.com', 'itunes.apple.com', 'smart.link'];

/** Whether `href` points at an app store listing (directly or via Kochava). */
export const isAppStoreUrl = (href: string): boolean => {
  try {
    const { hostname } = new URL(href);
    return STORE_HOSTS.includes(hostname.toLowerCase());
  } catch {
    return false;
  }
};
